import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class DataCacheService {
  private readonly DB_NAME = 'DataCacheDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'jsonFiles';

  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = this.openDB();
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;

        db.onversionchange = () => {
          console.warn('DB version change detected, closing connection');
          db.close();
          this.dbPromise = this.openDB(); // recreate connection
        };

        resolve(db);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ========================= GET =========================
  async get(key: string): Promise<any | null> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.get(key);

      req.onsuccess = () => resolve(req.result ?? null);

      req.onerror = () => {
        this.captureError(req.error, {
          operation: 'get',
          key,
        });
        reject(req.error);
      };
    });
  }

  // ========================= SET =========================
  async set(key: string, value: any): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.put(value, key);

      let settled = false;

      let valueSize = 0;
      try {
        valueSize = JSON.stringify(value)?.length || 0;
      } catch {
        valueSize = -1;
      }

      let _writeTimeoutId: number | null = null;

      const cleanup = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        req.onerror = null;

        if (_writeTimeoutId !== null) {
          clearTimeout(_writeTimeoutId as any);
          _writeTimeoutId = null;
        }
      };

      const fail = (err: any, stage: string) => {
        if (!settled) {
          settled = true;
          cleanup();

          this.captureError(err, {
            operation: 'set',
            key,
            stage,
            valueSize,
          });

          reject(err);
        }
      };

      tx.oncomplete = () => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve();
        }
      };

      tx.onerror = () => fail(tx.error, 'tx.onerror');
      tx.onabort = () => fail(tx.error, 'tx.onabort');
      req.onerror = () => fail(req.error, 'req.onerror');

      _writeTimeoutId = window.setTimeout(() => {
        fail(new Error('IndexedDB write timeout'), 'timeout');
      }, 10000);
    });
  }

  // ========================= REMOVE =========================
  async remove(key: string): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      store.delete(key);

      tx.oncomplete = () => resolve();

      tx.onerror = () => {
        this.captureError(tx.error, {
          operation: 'remove',
          key,
        });
        reject(tx.error);
      };

      tx.onabort = () => {
        this.captureError(tx.error, {
          operation: 'remove',
          key,
          stage: 'abort',
        });
        reject(tx.error);
      };
    });
  }

  // ========================= CLEAR =========================
  async clear(): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      store.clear();

      tx.oncomplete = () => resolve();

      tx.onerror = () => {
        this.captureError(tx.error, {
          operation: 'clear',
        });
        reject(tx.error);
      };

      tx.onabort = () => {
        this.captureError(tx.error, {
          operation: 'clear',
          stage: 'abort',
        });
        reject(tx.error);
      };
    });
  }

  // ========================= ERROR LOGGING =========================
  private async captureError(error: any, context: Record<string, any>) {
    const storageInfo = await this.getStorageInfo();
    const dbMeta = await this.getDBMeta();

    Sentry.withScope((scope) => {
      scope.setTag('module', 'indexeddb-cache');

      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (storageInfo) {
        Object.entries(storageInfo).forEach(([key, value]) => {
          scope.setExtra(`storage_${key}`, value);
        });
      }

      if (dbMeta) {
        Object.entries(dbMeta).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      scope.setExtra('userAgent', navigator.userAgent);

      Sentry.captureException(error);
    });
  }

  // ========================= STORAGE INFO =========================
  async getStorageInfo() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const { quota, usage } = await navigator.storage.estimate();

        return {
          quota,
          usage,
          usageInMB: usage ? (usage / 1024 / 1024).toFixed(2) : 0,
          quotaInMB: quota ? (quota / 1024 / 1024).toFixed(2) : 0,
          usagePercentage:
            quota && usage ? ((usage / quota) * 100).toFixed(2) : null,
        };
      }

      return { supported: false };
    } catch (err) {
      return { error: 'storage_info_failed' };
    }
  }

  // ========================= DB META =========================
  private async getDBMeta() {
    try {
      const db = await this.getDB();

      return {
        dbName: db.name,
        dbVersion: db.version,
        objectStores: Array.from(db.objectStoreNames),
      };
    } catch {
      return { dbError: 'not_initialized' };
    }
  }

  private async getDB(): Promise<IDBDatabase> {
    try {
      const db = await this.dbPromise;

      // basic sanity check
      if (!db.objectStoreNames.contains(this.STORE_NAME)) {
        throw new Error('DB invalid');
      }

      return db;
    } catch {
      this.dbPromise = this.openDB();
      return this.dbPromise;
    }
  }
}
